import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later, next, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { RequestError, Service } from 'webapp-types';
import { BillingFeature, Theme } from 'webapp/enums/enums';
import { TourId } from 'webapp/enums/tour';
import { TrackingEvent } from 'webapp/enums/tracking';
import { isAlive } from 'webapp/utils/common';

import eq from 'webapp/helpers/eq';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import horizontalScroll from 'webapp/modifiers/horizontal-scroll';
import willDestroy from 'webapp/modifiers/will-destroy';
import BoardImagePreviewComponent from 'webapp/components/board/image-preview';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormSelectWithActionComponent from 'webapp/components/form/select-with-action';
import ToolTipComponent from 'webapp/components/tool-tip';

import type Board from 'webapp/models-custom/board';
import BoardLoop from 'webapp/models-custom/board-loop';

import type { DesignerTourSettings } from 'webapp/services/flow/tour/designer';

interface Args {
	model: BoardLoop | null;
	activeBoard: Board;
	onBoardChange: (board: Board) => void;
	onLoopChange: (loop: BoardLoop) => void;
}

export default class DesignerLoopBoxComponent extends Component<Args> {
	@service('request') declare requestService: Service.Request;
	@service('popup') declare popupService: Service.Popup;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('tour') declare tourService: Service.Tour;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('flow/tour/designer') declare flowTourDesignerService: Service.Flow.Tour.Designer;

	@tracked isCreateChoiceShown = false;
	@tracked isDropdownOpen = false;
	@tracked selectedLoopBoards: Board[] = [];

	el!: JQuery;
	sortableEl: JQuery | null = null;
	availableTours: DesignerTourSettings[] = [
		{
			id: TourId.DESIGNER__ADD_TO_LOOP,
			checkStartConditions: this.checkEligibilityForAddToLoopDataboardTour.bind(this),
		},
	];

	get isLoopShown(): boolean {
		return !!this.args.model;
	}

	get isTemplate(): boolean {
		return !!this.args.model?.isTemplate;
	}

	get boardLoopTitle(): string | undefined {
		return this.args.model?.title;
	}

	get showCreateNewBoardForLoop(): boolean {
		return !this.args.model?.isFull;
	}

	get showRemoveBoardFromLoop(): boolean {
		return !!this.args.model && this.args.model.boardIds.length > 1;
	}

	@cached
	get allLoopsForBoard(): LabelValuePairString[] {
		const allLoops = this.mmBoardLoopsService.boardLoopModels.filter((boardLoop) => {
			return boardLoop.isBoardIncluded(this.args.activeBoard);
		});

		return allLoops.map((loop) => {
			return {
				value: loop.id!,
				label: loop.title,
			};
		});
	}

	@cached
	get existingBoardTemplateModels(): Board[] {
		return this.isTemplate
			? this.mmBoardsService.accountTemplateBoardModels
			: this.mmBoardsService.normalBoardModels;
	}

	get loopDropdownAction() {
		return [
			{
				id: null,
				text: 'New loop',
				html: '<a class="loop-add">New loop</a>',
				isSpecial: true,
				position: 'bottom',
				onClick: () => {
					const boardIds: number[] = [],
						activeBoardId = this.args.activeBoard?.id;

					if (activeBoardId) {
						boardIds.push(+activeBoardId);
					}

					const boardLoop = this.mmBoardLoopsService.createNewModel({
						boardIds,
					});

					this.mmBoardLoopsService.openEditPopup(boardLoop, 'designer');
				},
			},
		];
	}

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.setupSelectedLoopBoards();
	}

	@action
	toggleFavorite(): void {
		if (this.args.model) {
			this.mmBoardLoopsService.toggleFavorite(this.args.model);
		}
	}

	@action
	showCreateChoice(): void {
		this.flowTourDesignerService.endTour(TourId.DESIGNER__ADD_TO_LOOP);
		this.isCreateChoiceShown = true;
	}

	@action
	showAddBoardToLoopOptions(): void {
		const attachTo = this.el.find('.list .placeholder').get(0);
		const popup = this.popupService.createListPopup(attachTo, Theme.DARK);

		if (popup) {
			const itemsList: { text: string; icon: IconName; action: string }[] = [];

			if (!this.isTemplate) {
				itemsList.push({
					text: 'New blank dashboard',
					action: 'createNewBoardForLoop',
					icon: 'Plus',
				});
			}

			itemsList.push({
				text: 'Link with existing dashboard(s)..',
				action: 'addExistingBoardsToLoop',
				icon: 'Databoards',
			});

			popup.open({
				itemsList,
				doAction: (actionName: string) => {
					schedule('afterRender', () => {
						void this[
							actionName as 'createNewBoardForLoop' | 'addExistingBoardsToLoop'
						]();
					});
				},
			});
		}
	}

	@action
	async createNewBoardForLoop(): Promise<void> {
		const activeBoard = this.args.activeBoard;

		if (activeBoard.isNew) {
			try {
				await activeBoard.save();
			} catch (e) {
				this.requestService.showAllValidationErrors(e as RequestError);
				return;
			}
		}

		if (this.billingService.checkIfCan(BillingFeature.BOARDS, true)) {
			this.flowTourDesignerService.endHiddenTour();
			const infolayer = this.infolayerService.loading('Creating...');

			// create a board, then add current and the newly created one to the loop
			const newBoard = await this.createNewBoard();
			if (newBoard) {
				const loop = this.args.model;
				if (loop) {
					loop.boardIds = [...loop.boardIds, newBoard.numericId!];
					await loop
						.save()
						.then(() => {
							this.analyticsService.event(TrackingEvent.LOOP_EDITED, {
								origin: 'designer',
							});
						})
						.catch(
							this.requestService.showAllValidationErrors.bind(this.requestService),
						);
					infolayer.close();
				} else {
					const boardIds = [activeBoard.numericId!, newBoard.numericId!];

					let newLoop;
					try {
						newLoop = await this.createNewLoop(boardIds);
					} catch (_e) {
						// skip, infolayer was in method already
					}

					if (newLoop) {
						infolayer.ok('New looped dashboard created!');
					} else {
						infolayer.close();
						return;
					}
				}

				// navigate to board only after we have a loop saved, so loopbox shows the correct info
				await this.navigationService
					.transitionToRoute('designer.board', newBoard.id)
					.then(() => {
						this.resetSortable(true);
					});
			}
		}
	}

	async createNewBoard(): Promise<Board | null> {
		const newBoard = this.mmBoardsService.createNewModel();
		try {
			await newBoard.save();
		} catch (e) {
			this.requestService.showAllValidationErrors(e as RequestError);
			return null;
		}
		return newBoard;
	}

	async createNewLoop(boardIds: number[] = []): Promise<BoardLoop> {
		const newBoardLoop = this.mmBoardLoopsService.createNewModel({ boardIds });
		try {
			await newBoardLoop.save();
		} catch (e) {
			this.requestService.showAllValidationErrors(e as RequestError);
			return Promise.reject();
		}
		this.analyticsService.event(TrackingEvent.LOOP_CREATED, { origin: 'designer' });
		this.args.onLoopChange(newBoardLoop);

		return newBoardLoop;
	}

	checkEligibilityForAddToLoopDataboardTour(): boolean {
		return (
			isAlive(this) &&
			!this.popupService.anyPopupsOpen &&
			!this.isCreateChoiceShown &&
			this.args.activeBoard.allBlocks.length >= 2 &&
			this.mmBoardsService.normalBoardModels.length >= 2
		);
	}

	repositionTour(): void {
		const tour = this.flowTourDesignerService.activeTour;
		if (tour) {
			tour.repositionBubble();
		}
	}

	addExistingBoardsToLoop(): void {
		const loop = this.args.model;

		if (!loop) {
			return;
		}

		const alreadySelected = loop.boardIds,
			items = this.existingBoardTemplateModels.filter(
				(board) => !alreadySelected.includes(board.numericId!),
			),
			popup = this.mmBoardsService.createBoardsSelectListModal();

		popup.open({
			model: items,
			maxSelectable: BoardLoop.maxBoardsInLoop - alreadySelected.length,
			onConfirm: (selected: number[]) => {
				if (isAlive(this)) {
					loop.boardIds = [...alreadySelected, ...selected];
					loop.save()
						.finally(() => {
							this.resetSortable(true);
						})
						.catch(
							this.requestService.showAllValidationErrors.bind(this.requestService),
						);
				}
				return Promise.resolve(true);
			},
		});
	}

	setupSelectedLoopBoards(): void {
		this.selectedLoopBoards = Array.from(this.args.model?.boards || []);
	}

	setupSortable(): void {
		if (!this.el) {
			return;
		}

		if (!this.args.model) {
			return;
		}

		const sortableEl = this.el.find(`ol.list[data-loop-id='${this.args.model.id}']`);

		if (!sortableEl.length) {
			return;
		}

		sortableEl.sortable({
			items: 'li:not(.placeholder)',
			handle: '.handle',
			axis: 'x',
			containment: 'parent',
			tolerance: 'pointer', // otherwise containment parent cannot handle 1st or last sorts
			forcePlaceholderSize: true,
			helper: 'clone', // to prevent selecting board after finishing drag and drop
			placeholder: {
				element: function (_currentItem: JQuery) {
					return $('<li class="ui-sortable-placeholder"><div class="box"></div></li>');
				},
				update: function () {
					return;
				},
			},
			update: () => {
				this.updateOrderedItemsIds();
			},
		});

		this.sortableEl = sortableEl;
	}

	destroySortable(): void {
		const sortableEl = this.sortableEl;
		if (sortableEl?.sortable('instance')) {
			sortableEl.sortable('destroy');
			this.sortableEl = null;
		}
	}

	getOrderedItemIds(): number[] {
		const ids: number[] = [];

		this.sortableEl!.find('li:not(.placeholder)').each(
			(_index: number, element: HTMLElement) => {
				const id = $(element).attr('data-board-id');
				if (id) {
					ids.push(+id);
				}
			},
		);

		return ids;
	}

	updateOrderedItemsIds(): void {
		const model = this.args.model!,
			boardIds = this.getOrderedItemIds();

		model.directPropUpdate({ boardIds });
		const boardLoop = model.toSerializedJSON();

		this.requestService
			.api('PUT', `/board-loops/${model.id}`, { boardLoop })
			.then(() => {
				this.analyticsService.event(TrackingEvent.LOOP_EDITED, { origin: 'designer' });
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	onModelChange() {
		this.resetSortable(true);
	}

	@action
	changeBoard(board: Board): void {
		// close databoard edit name input
		(document.activeElement as HTMLElement | null)?.blur();

		this.flowTourDesignerService.endHiddenTour();
		this.args.onBoardChange(board);
	}

	@action
	resetSortable(moveScrollToEnd = false): void {
		this.selectedLoopBoards = [];
		this.destroySortable();

		next(() => {
			this.setupSelectedLoopBoards();
			this.setupSortable();

			if (moveScrollToEnd) {
				next(() => {
					const sortableEl = this.sortableEl;
					if (sortableEl?.length) {
						sortableEl[0]!.scrollLeft = sortableEl[0]!.scrollWidth;
					}
				});
			}
		});
	}

	@action
	linkWithExisting(): void {
		void this.mmBoardLoopsService
			.startCreateNewFlow('designer', this.args.activeBoard)
			.then((boardLoop) => {
				if (isAlive(this)) {
					this.args.onLoopChange(boardLoop);
				}
			});
	}

	@action
	openShareSettings(): void {
		const loop = this.args.model;
		if (loop) {
			this.mmBoardLoopsService.openSharePopup(loop, 'designer');
		}
	}

	@action
	openLoopFullscreen(): void {
		const loop = this.args.model;
		if (loop) {
			this.mmBoardLoopsService.openFullscreenView(loop);
		}
	}

	@action
	editSettings(): void {
		const loop = this.args.model;
		if (loop) {
			const initialBoardCount = loop.boardIds.length;
			this.mmBoardLoopsService.openEditPopup(loop, 'designer', () => {
				if (loop.boardIds.length !== initialBoardCount) {
					const currentBoard = this.args.activeBoard;
					const currentIndex = this.selectedLoopBoards.indexOf(currentBoard);
					const nextBoard =
						currentIndex === this.selectedLoopBoards.length - 1
							? this.selectedLoopBoards[currentIndex - 1]
							: this.selectedLoopBoards[currentIndex + 1];

					if (nextBoard) {
						this.changeBoard(nextBoard);
					}
				}
				this.resetSortable();
			});
		}
	}

	@action
	removeBoardFromLoop(board: Board): void {
		const loop = this.args.model;

		if (!loop) {
			return;
		}

		const affirmativeFunc = () => {
			let updatedBoardIds = Array.from(loop.boardIds);
			const boardId = board.numericId!,
				currentIndex = this.selectedLoopBoards.indexOf(board);

			const nextBoard =
				currentIndex === this.selectedLoopBoards.length - 1
					? this.selectedLoopBoards[currentIndex - 1]
					: this.selectedLoopBoards[currentIndex + 1];

			updatedBoardIds = updatedBoardIds.filter((b) => b !== boardId);
			loop.boardIds = updatedBoardIds;
			loop.save().catch(
				this.requestService.showAllValidationErrors.bind(this.requestService),
			);

			this.infolayerService.ok('Dashboard removed from loop.');

			this.changeBoard(nextBoard!);
			this.resetSortable();
		};

		this.popupService.openConfirmPopup({
			onConfirm: affirmativeFunc,
			title: `Do you want to remove dashboard "${board.name}" from the dashboard loop?`,
			confirmAction: 'Remove',
			cancelAction: 'Cancel',
		});
	}

	@action
	changeLoop(loopId: string): void {
		const boardLoop = this.mmBoardLoopsService.findModel(loopId);
		if (boardLoop) {
			this.args.onLoopChange(boardLoop);
		}
	}

	@action
	loopDropdownOpened() {
		this.isDropdownOpen = true;
		return false;
	}

	@action
	loopDropdownClosed(): void {
		this.isDropdownOpen = false;
	}

	@action
	repositionFn(e: Event): void {
		const node = this.el.get(0);

		const eventDetail = (e as CustomEvent<{ width: number; marginLeft: number } | null>).detail;

		if (!node || !eventDetail) {
			return;
		}

		node.style.width = `${eventDetail.width}px`;
		node.style.marginLeft = `${eventDetail.marginLeft}px`;

		later(this, this.repositionTour, 500);
	}

	@action
	setupView(node: HTMLElement): void {
		this.el = $(node);

		const previewEl = document.getElementsByClassName('view-board-inner')[0] as HTMLElement;
		if (previewEl) {
			previewEl.addEventListener('updatedPreviewPosition', this.repositionFn);
		}

		this.setupSortable();

		this.el
			.on('mouseenter', () => {
				later(() => {
					this.repositionTour();
				}, 300);
			})
			.on('mouseleave', () => {
				later(() => {
					this.repositionTour();
				}, 300);
			});

		this.availableTours.forEach((tourSettings) => {
			this.flowTourDesignerService.bindTourCheck(tourSettings.id, this, () => {
				if (tourSettings.checkStartConditions()) {
					this.flowTourDesignerService.startTour(tourSettings.id);
				}
			});
		});

		if (this.mmBoardLoopsService.normalBoardLoopModels.length) {
			this.flowTourDesignerService.endTour(TourId.DESIGNER__ADD_TO_LOOP);
		}
	}

	@action
	teardownView(): void {
		const previewEl = document.getElementsByClassName('view-board-inner')[0];
		if (previewEl && this.repositionFn) {
			previewEl.removeEventListener('updatedPreviewPosition', this.repositionFn);
		}
		this.destroySortable();

		this.availableTours.forEach((tourSettings) => {
			this.flowTourDesignerService.unbindTourCheck(tourSettings.id, this);
		});
	}
	<template>
		<div
			id="loop-box"
			class="{{if this.isLoopShown 'extended'}} {{if this.isDropdownOpen 'dropdown-open'}}"
			{{didInsert this.setupView}}
			{{didUpdate this.onModelChange @model}}
			{{willDestroy this.teardownView}}
		>

			{{#if this.isLoopShown}}
				<div class="list-ct">
					<div class="title">
						<CommonUiIconComponent @name="Loop" @size="md" />This dashboard is part of
						the
						{{#if this.isTemplate}}
							<strong>
								{{this.boardLoopTitle}}
							</strong>
						{{else}}
							<span>
								<FormSelectWithActionComponent
									name="loop"
									@selected={{@model.id}}
									@options={{this.allLoopsForBoard}}
									@actionOptions={{this.loopDropdownAction}}
									@onChange={{this.changeLoop}}
									@isNumeric={{false}}
									@isRequired={{true}}
									@showFloatlabel={{false}}
									@isDropdownWider={{true}}
									@extraClass="dropdown-loops"
									@onOpen={{this.loopDropdownOpened}}
									@onClose={{this.loopDropdownClosed}}
									@theme="dark"
									@size="tiny"
								/>
							</span>
						{{/if}}
						loop{{#if this.isTemplate}} template{{/if}}.
					</div>
					<ol data-loop-id={{@model.id}} class="list" {{horizontalScroll}}>
						{{#each this.selectedLoopBoards as |board|}}
							<li
								data-board-id={{board.id}}
								class={{if (eq @activeBoard.id board.id) "selected"}}
							>
								<div class="handle">
									<a
										class="open {{unless board.canCurrentUserEdit 'disabled'}}"
										{{on "click" (fn this.changeBoard board)}}
									>
										<BoardImagePreviewComponent
											@image={{board.image}}
											@color={{board.color}}
											@theme={{board.bgTheme}}
										/>
									</a>
									{{#if this.showRemoveBoardFromLoop}}
										<a
											class="remove"
											{{on "click" (fn this.removeBoardFromLoop board)}}
										>
											<CommonUiIconComponent @name="Close" @size="xs" />
										</a>
									{{/if}}
								</div>
								<ToolTipComponent @text={{board.name}} />
							</li>
						{{/each}}
						{{#if this.showCreateNewBoardForLoop}}
							<li
								class="placeholder"
								role="button"
								{{on "click" this.showAddBoardToLoopOptions}}
							>
								<span>+</span>
							</li>
						{{/if}}
					</ol>
					<div class="actions">
						{{#unless this.isTemplate}}
							<a {{on "click" this.openShareSettings}}>
								<CommonUiIconComponent @name="Share" @size="sm" />
								Share
							</a>
							<a class="link link--secondary" {{on "click" this.openLoopFullscreen}}>
								View loop
							</a>
						{{/unless}}
						<a class="link link--secondary" {{on "click" this.editSettings}}>Edit loop</a>
						<a class="favorite" {{on "click" this.toggleFavorite}}>
							<CommonUiIconComponent
								@name="StarFill"
								@size="sm"
								@class={{unless @model.favorite "gray"}}
							/>
							<ToolTipComponent @text={{@model.favoriteText}} />
						</a>
					</div>
				</div>
			{{else}}
				<div class="action-ct">
					<div class="title">
						<CommonUiIconComponent @name="Loop" @size="sm" />
						Looped dashboards
					</div>
					<div class="actions">
						{{#if this.isCreateChoiceShown}}
							<a class="box-plus" {{on "click" this.createNewBoardForLoop}}>
								<span class="box-plus__icon">
									<CommonUiIconComponent @name="Plus" @size="xs" />
								</span>
								New dashboard
							</a>
							<span></span>
							<a {{on "click" this.linkWithExisting}}>
								<CommonUiIconComponent @name="Databoards" @size="sm" />
								Link with existing dashboard
							</a>
						{{else}}
							<a class="single" {{on "click" this.showCreateChoice}}>
								<CommonUiIconComponent @name="Plus" @size="sm" />
								Extend with another dashboard...
							</a>
						{{/if}}
					</div>
				</div>
			{{/if}}

		</div>
	</template>
}
